import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import { authErrorKey, signUp } from "@/auth/cognito/client";
import { PRIVACY_POLICY_URL } from "@/lib/legalLinks";

import { AuthLayout } from "./AuthLayout";
import { Field, FormError, GoogleButton, PasswordField, SubmitButton } from "./fields";

/**
 * Create an account.
 *
 * <p>First and last name are <b>required</b> and go in as {@code given_name} /
 * {@code family_name}, the same standard claims the backend's
 * {@code CurrentUserService} reads when it provisions the {@code app_user} row.
 * Capturing them here (not on the later profile-setup screen) is what lets that
 * screen show them read-only instead of asking again.</p>
 *
 * <p>The requirement is enforced <b>here and in the app</b>, not in Cognito: a
 * standard attribute's required-ness is fixed when the pool is created and our
 * live pool has these optional, so it can't be changed retroactively. Accounts
 * that predate this (or arrive without names via a future federated IdP) are
 * handled by the profile-setup screen's editable fallback.</p>
 *
 * <p>Signing up does not grant access: the account lands on the waitlist until
 * an admin approves it (the approval gate). Saying so here is deliberate —
 * finding out only after confirming an email would feel like a bait.</p>
 */
export function SignUpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signUp({ email, password, givenName, familyName });
      navigate(`/signup/confirm?email=${encodeURIComponent(email.trim())}`);
    } catch (err) {
      setError(t(authErrorKey((err as { code?: string }).code ?? "UnknownError")));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={t("auth.signUp.title")}
      footer={
        <>
          {t("auth.signUp.haveAccount")}{" "}
          <Link to="/signin" className="font-medium text-teal-700 hover:text-teal-800">
            {t("auth.signUp.signInInstead")}
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t("auth.fields.firstName")}
            value={givenName}
            onChange={(e) => setGivenName(e.target.value)}
            autoComplete="given-name"
            autoFocus
            required
          />
          <Field
            label={t("auth.fields.lastName")}
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            autoComplete="family-name"
            required
          />
        </div>
        <Field
          label={t("auth.fields.email")}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
        <PasswordField
          label={t("auth.fields.password")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
          minLength={8}
        />
        <p className="text-xs text-slate-500">{t("auth.passwordRules")}</p>
        <FormError>{error}</FormError>
        <SubmitButton busy={busy}>{t("auth.signUp.submit")}</SubmitButton>
        <p className="text-xs leading-relaxed text-slate-500">
          <Trans
            i18nKey="auth.signUp.legal"
            components={{
              // No `terms` link here on purpose: acceptance moved to the blocking TermsGate,
              // where it is an explicit tick and is recorded. This sentence covers privacy and
              // cookies, which apply from the moment the account exists.
              privacy: (
                <a
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:text-slate-700"
                />
              ),
              cookies: <Link to="/cookies" className="underline hover:text-slate-700" />,
            }}
          />
        </p>
      </form>
      <GoogleButton />
    </AuthLayout>
  );
}
